import { useEffect, useMemo, useRef } from 'react';
import { ChakraSkeleton, ChakraStack, DropdownV2, Text } from '@equip.health/ui';
import { useGetAppointmentTypesQuery } from '~/store/services/schedule';
import { CareTeamProviderType } from '~/lib/constants/patients.constants';
import SelectableUserAvatarBlock from '../SelectableUserAvatarBlock';
import { getEnrichedUser } from '../utils';
import {
  DEFAULT_RECURRING_DURATION,
  DEFAULT_RECURRING_DURATION_FALLBACK,
  defaultRecurringAppointmentGroups,
  RECURRING_SESSION_DURATIONS,
  RECURRING_SESSION_TYPES,
} from '../constants';

interface RecurringTypeAndAttendeesProps {
  providerType: string;
  selectedAppointmentType: Partial<AppointmentType> | null;
  onAppointmentTypeChange: (appointmentType: Partial<AppointmentType>) => void;
  attendees: FamilyAttendeeWithSelection[];
  onAttendeesChange: (attendees: FamilyAttendeeWithSelection[]) => void;
  patient?: Patient;
}

export default function RecurringTypeAndAttendees({
  providerType,
  selectedAppointmentType,
  onAppointmentTypeChange,
  attendees,
  onAttendeesChange,
  patient,
}: RecurringTypeAndAttendeesProps) {
  const { data: appointmentTypesData, isLoading: isLoadingAppointmentTypes } =
    useGetAppointmentTypesQuery(providerType);
  const hasAutoSelectedRef = useRef(false);

  const preferredDuration =
    DEFAULT_RECURRING_DURATION[providerType as CareTeamProviderType] ??
    DEFAULT_RECURRING_DURATION_FALLBACK;

  const appointmentTypeOptions = useMemo(() => {
    if (!appointmentTypesData) return [];

    const filtered = appointmentTypesData.filter(
      (type) =>
        RECURRING_SESSION_TYPES.has(type.appointmentType) &&
        RECURRING_SESSION_DURATIONS.has(type.durationInMinutes),
    );

    return filtered
      .sort((a, b) => {
        const aPreferred = a.durationInMinutes === preferredDuration ? 0 : 1;
        const bPreferred = b.durationInMinutes === preferredDuration ? 0 : 1;
        if (aPreferred !== bPreferred) return aPreferred - bPreferred;
        return a.durationInMinutes - b.durationInMinutes;
      })
      .map((type) => ({
        label: `${type.appointmentType}, ${type.durationInMinutes} minutes`,
        value: type.appointmentTypeExtId,
        data: {
          ...type,
          appointmentGroup:
            defaultRecurringAppointmentGroups[
              providerType as CareTeamProviderType
            ] ?? type.appointmentGroup,
        },
      }));
  }, [appointmentTypesData, preferredDuration, providerType]);

  useEffect(() => {
    if (
      !appointmentTypeOptions.length ||
      selectedAppointmentType ||
      hasAutoSelectedRef.current
    ) {
      return;
    }

    if (appointmentTypeOptions[0]) {
      hasAutoSelectedRef.current = true;
      onAppointmentTypeChange(appointmentTypeOptions[0].data);
    }
  }, [appointmentTypeOptions, selectedAppointmentType, onAppointmentTypeChange]);

  const handleAppointmentTypeSelect = (value: string) => {
    const selected = appointmentTypeOptions.find((opt) => opt.value === value);
    if (selected) {
      onAppointmentTypeChange(selected.data);
    }
  };

  const handleAttendeeToggle = (attendeeId: string) => {
    const updated = attendees.map((attendee) =>
      attendee.externalId === attendeeId
        ? { ...attendee, isSelected: !attendee.isSelected }
        : attendee,
    );
    onAttendeesChange(updated);
  };

  if (isLoadingAppointmentTypes) {
    return (
      <ChakraStack spacing={6}>
        <ChakraSkeleton height="60px" />
        <ChakraSkeleton height="200px" />
      </ChakraStack>
    );
  }

  const isReadOnly = appointmentTypeOptions.length === 1;

  return (
    <ChakraStack spacing={6}>
      <Text color="neutral.secondary" variant="caption">
        Please choose the next appointment type and attendees you'd like to
        schedule next.
      </Text>

      <DropdownV2
        id="recurring-appointment-type"
        isDisabled={isReadOnly}
        isResettable={false}
        menuOptions={appointmentTypeOptions}
        onOptionClick={handleAppointmentTypeSelect}
        value={selectedAppointmentType?.appointmentTypeExtId || ''}
      />

      <ChakraStack spacing={3}>
        {[...attendees]
          .sort((a, b) => Number(b.isRequired) - Number(a.isRequired))
          .map((attendee) => (
            <SelectableUserAvatarBlock
              key={attendee.externalId}
              id={attendee.externalId}
              user={getEnrichedUser(attendee, patient)}
              isSelected={attendee.isSelected}
              onToggle={() => handleAttendeeToggle(attendee.externalId)}
              variant="attendeeCard"
            />
          ))}
      </ChakraStack>
    </ChakraStack>
  );
}
